// Map env vars to internal configuration

import { parseStytchDomain } from "@auger/identity"
import { parseIdentityProviders } from "@/identity/providers"
import { normalizeReturnOrigins } from "@/identity/returnTo"

class ConfigError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "ConfigError"
  }
}

export const api = {
  // absolute URL
  url: import.meta.env.VITE_HEAVYBIT_API,
  // hasura admin secret (dev only use)
  adminSecret: import.meta.env.VITE_HEAVYBIT_API_SECRET,
}

if (!api.url) throw new ConfigError("VITE_HEAVYBIT_API must be in environment")

// Auger signs in through Heavybit Identity (Stytch,
// identity/stytchSession.ts) and nothing else. The public token is
// required unless auth is disabled — see the check below `identity`.

// VITE_AUTH_DISABLED is the dev/test bypass; VITE_AUTH0_DISABLED is its old,
// provider-specific name. Both are honoured for one release; the legacy name
// logs once so .env files get updated.
const legacyAuthDisabled = !!import.meta.env.VITE_AUTH0_DISABLED
const authDisabled = !!import.meta.env.VITE_AUTH_DISABLED || legacyAuthDisabled
if (legacyAuthDisabled && !import.meta.env.VITE_AUTH_DISABLED) {
  console.warn(
    "[config] VITE_AUTH0_DISABLED is deprecated; set VITE_AUTH_DISABLED=true instead (both work for this release)."
  )
}

export const auth = {
  // disable authentication protection (dev only use)
  disabled: authDisabled,
}

// Heavybit Identity (Stytch B2B) front door served under /identity/*. This is
// the sign-in, exchange, and Connected Apps consent surface for the whole
// Stytch project (Auger, its MCP server, Crucible), and Auger's own sign-in.
// With no public token (the dev/test bypass only) the routes still exist and
// render a "not configured" notice.
export const identity = {
  stytchPublicToken: import.meta.env.VITE_STYTCH_PUBLIC_TOKEN || undefined,
  enabled: !!import.meta.env.VITE_STYTCH_PUBLIC_TOKEN,
  // The project's custom auth domain (Test: https://id-test.heavybit.com,
  // Live: https://id.heavybit.com). Every browser SDK call goes through it so
  // the tokens Stytch mints, and the `iss` on OAuth callbacks, carry this
  // domain as issuer. Required whenever the public token is set — see below.
  stytchDomain: parseStytchDomain(import.meta.env.VITE_STYTCH_DOMAIN),
  // Other apps on the project may send a person here and want them back:
  // Crucible's /signup lands on /identity/login?return_to=https://crucible…/signed-up.
  // Only origins listed here (comma-separated, scheme + host) are followed;
  // everything else falls back to /identity/done.
  returnOrigins: normalizeReturnOrigins(import.meta.env.VITE_IDENTITY_RETURN_ORIGINS),
  // Which sign-in buttons the login page offers, comma-separated. Every
  // environment sets "google,github"; UNSET means Google only. Never required:
  // a bad value warns and falls back, it does not throw (identity/providers.ts).
  providers: parseIdentityProviders(import.meta.env.VITE_IDENTITY_PROVIDERS),
}

// Without the public token there is no way to sign in. Before the Auth0
// decommission its absence silently produced an Auth0 build; now it fails the
// boot, so a Netlify context that loses the variable breaks loudly.
if (!auth.disabled && !identity.enabled) {
  throw new ConfigError("VITE_STYTCH_PUBLIC_TOKEN must be in environment")
}

// The domain is not optional once the public token is set, and the failure it
// causes is nasty enough to be worth failing the boot for. Stytch derives a
// token's `iss` from the HOST that was called. The server exchange
// (auth-session) uses STYTCH_DOMAIN, so the first login works and the first
// JWT carries the custom-domain issuer. The browser SDK then re-mints that JWT
// every five minutes — on the default Stytch host, with
// `iss = stytch.com/<project id>`, which Hasura's single configured issuer
// rejects. The app looks fine for five minutes and then every query fails
// while forceRefreshToken re-mints the same rejected token in a loop.
if (identity.enabled && !identity.stytchDomain) {
  throw new ConfigError(
    "VITE_STYTCH_DOMAIN must be in environment when VITE_STYTCH_PUBLIC_TOKEN is set " +
      "(the browser SDK must mint tokens on the same host Hasura trusts as issuer)"
  )
}

export const general = {
  // user to connect as instead of using a JWT (dev only use)
  // In test mode, smoke tests may override this via localStorage('smoke:userId')
  // to simulate different users without rebuilding the app.
  forcedUser:
    (typeof localStorage !== "undefined" && localStorage.getItem("smoke:userId")) ||
    import.meta.env.VITE_FORCE_USER,
  // The forced user's person record id, sent as
  // x-hasura-person-id. smoke:personId overrides it beside smoke:userId.
  forcedPerson:
    (typeof localStorage !== "undefined" && localStorage.getItem("smoke:personId")) ||
    import.meta.env.VITE_FORCE_PERSON ||
    undefined,
}

export const google = {
  // API key for public APIs, private ones use OAuth2 auth
  apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
  // Client ID for google API access
  clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
}

if (!google.apiKey) throw new ConfigError("VITE_GOOGLE_API_KEY must be in environment")
if (!google.clientId) throw new ConfigError("VITE_GOOGLE_CLIENT_ID must be in environment")

export const posthog = {
  api_key: import.meta.env.VITE_POSTHOG_API_KEY,
}

export default {
  api,
  auth,
  identity,
  general,
  google,
}
